/// <amd-module name="Repos/Views/PullRequest/Components/ActivityFeed/ActivityCards/ReviewersUpdatedCard" />
import * as React from "react";
import * as Resources from "Repos/Views/PullRequest/Resources";
import { equals } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { Components } from "VSS/Platform/Layout";
import { getIdentity, getIdentityComponent, getPropertyValue } from "../Utils/ActivityFeedUtils";
import { ActivityCardSubtle, IActivityCardSharedProps } from "./ActivityCardSubtle";
import { ActivityShimmer } from "./ActivityShimmer";

export const ReviewersUpdatedCard: React.FunctionComponent<IActivityCardSharedProps> = props => {
    const { isShim } = props;
    if (!isShim) {
        const updaterId = getPropertyValue(props.thread, "CodeReviewReviewersUpdatedByIdentity");
        const updater = getIdentity(props.thread, updaterId);

        const numAdded: number = parseInt(getPropertyValue(props.thread, "CodeReviewReviewersUpdatedNumAdded", "0"));
        const numChanged: number = parseInt(getPropertyValue(props.thread, "CodeReviewReviewersUpdatedNumChanged", "0"));
        const numDeclined: number = parseInt(getPropertyValue(props.thread, "CodeReviewReviewersUpdatedNumDeclined", "0"));
        const numRemoved: number = parseInt(getPropertyValue(props.thread, "CodeReviewReviewersUpdatedNumRemoved", "0"));

        if (!updater || numAdded + numChanged + numDeclined + numRemoved === 0) {
            return null;
        }

        let content: JSX.Element | null = null;
        const updaterComponent: JSX.Element = getIdentityComponent(updater, "activity-card-first-image");

        const addedRemovedCount = numAdded + numRemoved;
        if (addedRemovedCount > 0) {
            if (addedRemovedCount === 1) {
                const reviewerAdded = numAdded === 1;

                const reviewer = getIdentity(
                    props.thread,
                    getPropertyValue(
                        props.thread,
                        reviewerAdded ? "CodeReviewReviewersUpdatedAddedIdentity" : "CodeReviewReviewersUpdatedRemovedIdentity"
                    )
                );

                if (reviewer) {
                    if (reviewer.id === updater.id) {
                        // If joined just to decline, don't show "joined as a reviewer", instead show only the "declined to review" added below
                        if (numDeclined === 0) {
                            const format = reviewerAdded ? Resources.ActivityFeedReviewerJoined : Resources.ActivityFeedReviewerQuit;

                            content = (
                                <FormatComponent elementType={null} format={format}>
                                    {updaterComponent}
                                </FormatComponent>
                            );
                        }
                    } else {
                        const format = reviewerAdded ? Resources.ActivityFeedReviewerAddedBy : Resources.ActivityFeedReviewerRemovedBy;

                        const reviewerComponent = getIdentityComponent(reviewer, "margin-horizontal-8");

                        content = (
                            <FormatComponent elementType={null} format={format}>
                                {updaterComponent}
                                {reviewerComponent}
                            </FormatComponent>
                        );
                    }
                }
            } else if (numRemoved === 0) {
                content = (
                    <FormatComponent elementType={null} format={Resources.ActivityFeedReviewersAdded}>
                        {updaterComponent}
                        {numAdded.toString()}
                    </FormatComponent>
                );
            } else if (numAdded === 0) {
                content = (
                    <FormatComponent elementType={null} format={Resources.ActivityFeedReviewersRemoved}>
                        {updaterComponent}
                        {numRemoved.toString()}
                    </FormatComponent>
                );
            } else {
                content = (
                    <FormatComponent elementType={null} format={Resources.ActivityFeedReviewersAddedRemoved}>
                        {updaterComponent}
                        {numAdded.toString()}
                        {numAdded === 1 ? Resources.ActivityFeedReviewer : Resources.ActivityFeedReviewers}
                        {numRemoved.toString()}
                        {numRemoved === 1 ? Resources.ActivityFeedReviewer : Resources.ActivityFeedReviewers}
                    </FormatComponent>
                );
            }
        }

        if (numChanged > 0 || numDeclined > 0) {
            const reviewer = getIdentity(props.thread, getPropertyValue(props.thread, "CodeReviewReviewersUpdatedChangedIdentity"));
            const changedToRequired = equals(getPropertyValue(props.thread, "CodeReviewReviewersUpdatedChangedToRequired", "false"), "true", true);

            if (reviewer) {
                let changedContent: JSX.Element | null = null;
                if (numChanged > 0) {
                    if (reviewer.id === updater.id) {
                        changedContent = (
                            <FormatComponent
                                elementType={null}
                                format={
                                    changedToRequired
                                        ? Resources.ActivityFeedReviewerChangeToRequiredSelf
                                        : Resources.ActivityFeedReviewerChangeToOptionalSelf
                                }
                            >
                                {updaterComponent}
                            </FormatComponent>
                        );
                    } else {
                        changedContent = (
                            <FormatComponent
                                elementType={null}
                                format={
                                    changedToRequired
                                        ? Resources.ActivityFeedReviewerChangeToRequired
                                        : Resources.ActivityFeedReviewerChangeToOptional
                                }
                            >
                                {updaterComponent}
                                {getIdentityComponent(reviewer, "margin-horizontal-8")}
                            </FormatComponent>
                        );
                    }
                }

                if (numDeclined > 0) {
                    const changedToDecline = equals(
                        getPropertyValue(props.thread, "CodeReviewReviewersUpdatedChangedDecline", "false"),
                        "true",
                        true
                    );
                    const declinedContent = (
                        <FormatComponent
                            elementType={null}
                            format={changedToDecline ? Resources.ActivityFeedReviewerDeclined : Resources.ActivityFeedReviewerRejoined}
                        >
                            {updaterComponent}
                        </FormatComponent>
                    );

                    changedContent = changedContent ? (
                        <FormatComponent elementType={null} format={Resources.ActivityFeedReviewersAddedRemovedChanged}>
                            {declinedContent}
                            {changedContent}
                        </FormatComponent>
                    ) : (
                        declinedContent
                    );
                }

                if (changedContent) {
                    content = content ? (
                        <FormatComponent elementType={null} format={Resources.ActivityFeedReviewersAddedRemovedChanged}>
                            {content}
                            {changedContent}
                        </FormatComponent>
                    ) : (
                        changedContent
                    );
                }
            }
        }

        return <ActivityCardSubtle createDate={props.thread.publishedDate}>{content}</ActivityCardSubtle>;
    } else {
        return (
            <ActivityCardSubtle createDate={props.thread.publishedDate}>
                <ActivityShimmer text />
            </ActivityCardSubtle>
        );
    }
};

Components.add("ms.vss-code-web.reviewers-updated-activity-card", ReviewersUpdatedCard);
